import { Button, DateText, useConfirm, useToast } from '@/components';
import { ApiClientError } from '@uchat-team-management/shared';
import { Show } from 'solid-js';
import { useStartSlackLink, useUnlinkSlack } from './hooks';

/**
 * Personal settings card: link a Slack account so the bot can DM release-build events
 * (UCH-805). Connect is a full-page redirect through Sign in with Slack; the
 * API bounces code + state back to /profile/settings, where
 * PersonalSettingsPage completes the link and toasts the outcome.
 */
export const MySlackSection = (props: {
  slackUserId: string | null;
  slackLinkedAt: string | null;
  slackOpenUrl: string | null;
}) => {
  const start = useStartSlackLink();
  const unlink = useUnlinkSlack();
  const toast = useToast();
  const confirmDialog = useConfirm();

  const onConnect = async () => {
    try {
      window.location.assign(await start.mutateAsync());
    } catch (err) {
      toast.error(
        err instanceof ApiClientError ? err.error.message : 'Failed to start Slack linking',
      );
    }
  };

  const onUnlink = async () => {
    const ok = await confirmDialog({
      title: 'Unlink Slack?',
      body: 'You will stop receiving release DMs until you connect again.',
      confirmLabel: 'Unlink',
      danger: true,
    });
    if (!ok) return;
    try {
      await unlink.mutateAsync();
      toast.success('Slack unlinked');
    } catch {
      toast.error('Failed to unlink Slack');
    }
  };

  return (
    <div class="flex flex-col gap-4">
      <p class="text-sm text-neutral-600 dark:text-neutral-400">
        Link your Slack account to get direct messages when releases you create, confirm, or watch
        start, finish, or are published.
      </p>
      <Show
        when={props.slackUserId}
        fallback={
          <div>
            <Button onClick={() => void onConnect()} loading={start.isPending}>
              Connect Slack
            </Button>
          </div>
        }
      >
        <div class="flex flex-wrap items-center gap-3">
          <p class="text-sm">
            Linked as <code>{props.slackUserId}</code>
            <Show when={props.slackLinkedAt}>
              {(at) => (
                <span class="text-neutral-500">
                  {' '}
                  on <DateText value={at()} />
                </span>
              )}
            </Show>
          </p>
          <Show when={props.slackOpenUrl}>
            {(url) => (
              <a
                href={url()}
                target="_blank"
                rel="noreferrer"
                class="text-brand-600 text-sm hover:underline dark:text-brand-400"
              >
                Open in Slack
              </a>
            )}
          </Show>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void onUnlink()}
            loading={unlink.isPending}
          >
            Unlink
          </Button>
        </div>
      </Show>
    </div>
  );
};
